// Upload flow for a client context document (ADR-0173 fase 2, PR-6).
// No React: the drawer owns its state, this module owns the wire and the
// user-facing vocabulary (kinds, statuses, failure reasons in es-CL).
//
// Three steps, same shape as the logo upload (features/admin/logoUpload):
//   1. sha256 of the bytes in the browser (crypto.subtle) — the server
//      dedupes per brand on it and the worker re-checks it after download;
//   2. POST /context-sources/upload-url → presigned PUT into the PRIVATE
//      bucket; the PUT replays requiredHeaders verbatim, no Authorization;
//   3. POST /context-sources → HEAD-verified registration + extraction job.

import type { ContextExtractionFailureReason, ContextSourceStatus } from "@bitacorapro/core-engine";
import type { ContextSource, RecipeMeResponse } from "@bitacorapro/api-types";
import { contextApi } from "../../lib/api/endpoints/context";
import { ApiError } from "../../lib/api/error";

export const CONTEXT_MAX_BYTES = 20 * 1024 * 1024;

export type ContextSourceKindView = {
  key: string;
  label: string;
  mimeTypes: readonly string[];
  validity: "dated" | "until_replaced";
  produces: "rules" | "visual_refs";
};

/** The kinds a brand can upload TODAY: text documents that produce
 *  rules. Image kinds (visual references) are fase 3 and are not offered
 *  rather than shown disabled (founder: hide unusable actions). */
export function uploadableKinds(recipe: RecipeMeResponse | undefined): ContextSourceKindView[] {
  const kinds = (recipe as { contextSourceKinds?: ContextSourceKindView[] } | undefined)
    ?.contextSourceKinds;
  return (kinds ?? []).filter((k) => k.produces === "rules");
}

export function kindLabel(recipe: RecipeMeResponse | undefined, key: string): string {
  const kinds = (recipe as { contextSourceKinds?: ContextSourceKindView[] } | undefined)
    ?.contextSourceKinds;
  return kinds?.find((k) => k.key === key)?.label ?? key;
}

export const INVALID_TYPE_MESSAGE =
  "Este archivo no sirve para este tipo de documento. Revisa el formato permitido.";
export const TOO_LARGE_MESSAGE = "El archivo supera los 20 MB.";
export const PUT_FAILED_MESSAGE =
  "No pudimos subir el documento. Revisa tu conexión e inténtalo de nuevo.";
export const DUPLICATE_MESSAGE =
  "Este documento ya fue subido para esta marca. Archiva la versión anterior si quieres reemplazarla.";
export const SESSION_EXPIRED_MESSAGE = "Tu sesión venció. Vuelve a iniciar sesión.";
export const QUOTA_MESSAGE =
  "La organización ya subió el máximo de documentos de hoy. Inténtalo mañana.";

export function validateContextFile(file: File, kind: ContextSourceKindView): string | null {
  if (!kind.mimeTypes.includes(file.type)) return INVALID_TYPE_MESSAGE;
  if (file.size <= 0 || file.size > CONTEXT_MAX_BYTES) return TOO_LARGE_MESSAGE;
  return null;
}

export async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export type UploadContextSourceInput = {
  brandId: string;
  kind: ContextSourceKindView;
  file: File;
  validFrom?: string | undefined;
  validTo?: string | undefined;
  onProgress?: (step: "hashing" | "uploading" | "registering") => void;
};

export async function uploadContextSource(input: UploadContextSourceInput): Promise<ContextSource> {
  const invalid = validateContextFile(input.file, input.kind);
  if (invalid) throw new Error(invalid);
  try {
    input.onProgress?.("hashing");
    const sha256 = await sha256Hex(input.file);
    const presign = await contextApi.requestUploadUrl({
      brandId: input.brandId,
      kind: input.kind.key,
      contentType: input.file.type,
      contentLength: input.file.size,
    });
    input.onProgress?.("uploading");
    let putOk = false;
    try {
      const putRes = await fetch(presign.uploadUrl, {
        method: "PUT",
        body: input.file,
        headers: presign.requiredHeaders,
      });
      putOk = putRes.ok;
    } catch {
      putOk = false;
    }
    if (!putOk) throw new Error(PUT_FAILED_MESSAGE);
    input.onProgress?.("registering");
    return await contextApi.registerSource({
      brandId: input.brandId,
      kind: input.kind.key,
      objectKey: presign.objectKey,
      sha256,
      fileName: input.file.name.slice(0, 200),
      ...(input.validFrom ? { validFrom: new Date(input.validFrom).toISOString() } : {}),
      ...(input.validTo ? { validTo: new Date(input.validTo).toISOString() } : {}),
    });
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 401) throw new Error(SESSION_EXPIRED_MESSAGE);
      if (err.code === "CONTEXT_SOURCE_DUPLICATE") throw new Error(DUPLICATE_MESSAGE);
      if (err.code === "CONTEXT_EXTRACT_QUOTA_EXCEEDED") throw new Error(QUOTA_MESSAGE);
      throw new Error(err.message);
    }
    throw err;
  }
}

export function statusLabel(status: ContextSourceStatus): string {
  return {
    uploaded: "Subido, en cola",
    extracting: "Extrayendo reglas",
    in_review: "Por revisar",
    active: "Activo",
    archived: "Archivado",
    failed: "Falló",
  }[status];
}

/** The tone class the shared `.st-pill--*` vocabulary already has. */
export function statusTone(status: ContextSourceStatus): "ok" | "warn" | "crit" | "idle" {
  switch (status) {
    case "active":
      return "ok";
    case "in_review":
      return "warn";
    case "failed":
      return "crit";
    default:
      return "idle";
  }
}

export function failureReasonLabel(reason: ContextExtractionFailureReason | null): string {
  if (!reason) return "";
  return {
    download_failed: "No pudimos descargar el archivo del almacenamiento.",
    magic_bytes: "El contenido del archivo no coincide con su tipo declarado.",
    xlsx_macros: "La planilla trae macros. Guárdala como .xlsx sin macros y vuelve a subirla.",
    xlsx_external_links: "La planilla tiene enlaces externos u objetos incrustados.",
    xlsx_zip_bomb: "La planilla es demasiado grande al descomprimir. Divídela.",
    pdf_javascript: "El PDF contiene scripts o adjuntos. Vuelve a exportarlo («imprimir a PDF»).",
    pdf_too_many_pages: "El PDF supera las 20 páginas. Sube solo el capítulo de estándares.",
    pdf_encrypted: "El PDF está protegido con contraseña.",
    pdf_no_text: "El PDF no tiene texto seleccionable (es un escaneo). Pide el original.",
    unsupported_kind: "Este tipo de documento todavía no se procesa.",
    provider_error: "El servicio de IA falló. Vuelve a intentar.",
    schema_invalid: "La respuesta del extractor no fue válida. Vuelve a intentar.",
    timeout: "La extracción tardó demasiado y se canceló. Vuelve a intentar.",
  }[reason];
}

export const RETRYABLE_REASONS: ReadonlySet<ContextExtractionFailureReason> = new Set([
  "download_failed",
  "provider_error",
  "schema_invalid",
  "timeout",
]);
